import React from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowTrendingUpIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  UserIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import * as API from "../../api";

/**
 * Become a Partner — redesigned to match the Tailwind design system used on
 * Home/Contact (eyebrow badge + 2-line display title, brand/accent colors,
 * rounded cards, shadow-card). Same destinations as the legacy MUI page:
 * login → `${API.URL}/partner`, registration → `${API.URL}/partner/register`,
 * contact via partenaires@tafwija.com and WhatsApp 0689771647.
 */

const LOGIN_URL = `${API.PARTNER_URL}/login`;
const REGISTER_URL = `${API.PARTNER_URL}/register`;
const CONTACT_EMAIL = "partenaires@tafwija.com";
const WHATSAPP_PHONE = "0689771647";

const BENEFITS = [
  {
    icon: ArrowTrendingUpIcon,
    titleKey: "partners.benefit1Title",
    titleDefault: "Élargir votre marché",
    bodyKey: "partners.benefit1Body",
    bodyDefault:
      "Bénéficiez d'un référencement à l'échelle nationale et obtenez plus de visibilité pour vos activités. Nous assurons la promotion et la distribution via nos différents canaux, payants et gratuits.",
  },
  {
    icon: DevicePhoneMobileIcon,
    titleKey: "partners.benefit2Title",
    titleDefault: "Digitaliser le parcours client",
    bodyKey: "partners.benefit2Body",
    bodyDefault:
      "Digitalisez votre processus de réservation et facilitez le parcours client. Gérez vos réservations directement depuis votre espace privé et gardez la trace de toutes vos opérations.",
  },
  {
    icon: ShieldCheckIcon,
    titleKey: "partners.benefit3Title",
    titleDefault: "Fiabiliser votre business",
    bodyKey: "partners.benefit3Body",
    bodyDefault:
      "De grandes marques nous font déjà confiance. Associer votre nom aux leurs renforce le niveau de fiabilité de votre établissement et de vos produits.",
  },
];

const STEPS = [
  { key: "partners.step1", def: "Créez votre compte partenaire gratuitement" },
  { key: "partners.step2", def: "Publiez vos activités et fixez vos prix" },
  { key: "partners.step3", def: "Recevez des réservations et gérez tout en ligne" },
];

function BenefitCard({ benefit, t }) {
  const Icon = benefit.icon;
  return (
    <div className="group rounded-2xl border border-slate-100 bg-white p-6 shadow-card transition-all duration-200 hover:border-brand-200 hover:shadow-card-hover sm:p-7">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-500 transition-transform duration-300 group-hover:scale-105">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="mt-5 font-display text-lg font-semibold text-ink">
        {t(benefit.titleKey, benefit.titleDefault)}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-ink-muted">
        {t(benefit.bodyKey, benefit.bodyDefault)}
      </p>
    </div>
  );
}

export default function Partners() {
  const { t } = useTranslation();

  return (
    <div className="bg-surface">
      {/* Hero */}
      <section className="relative overflow-hidden bg-surface-muted">
        {/* soft accent halo */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 end-[-6rem] h-72 w-72 rounded-full bg-accent-500/20 blur-3xl"
        />
        <div className="relative mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 lg:pt-16 pb-10 sm:pb-14">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700 shadow-card border border-slate-200/80">
              <SparklesIcon className="h-3.5 w-3.5 text-accent-500" />
              {t("partners.eyebrow", "Devenez partenaire")}
            </span>
            <h1 className="mt-4 font-display font-extrabold text-ink leading-[1.15] tracking-tight text-[1.75rem] sm:text-4xl lg:text-5xl">
              <span className="block text-balance">
                {t("partners.heroTitle", "Atteignez des milliers de clients")}
              </span>
              <span className="inline-block text-brand-500 mt-1.5">
                {t("partners.heroTitleAccent", "avec Tafwija.")}
              </span>
            </h1>
            <p className="mt-4 text-sm sm:text-base text-ink-muted leading-relaxed">
              {t(
                "partners.heroSubtitle",
                "Référencez vos activités, voyages et séjours sur la plateforme de référence des expériences au Maroc. Plus de visibilité, plus de réservations, moins de gestion."
              )}
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={REGISTER_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-6 font-semibold text-white shadow-card transition-colors hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 sm:w-auto"
              >
                <span>{t("partners.ctaRegister", "Inscription gratuite")}</span>
                <ArrowRightIcon className="h-4 w-4 rtl:-scale-x-100" />
              </a>
              <a
                href={LOGIN_URL}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 font-semibold text-ink shadow-card transition-colors hover:border-slate-300 hover:bg-surface-subtle sm:w-auto"
              >
                <UserIcon className="h-4 w-4" />
                <span>{t("partners.ctaLogin", "Se connecter")}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-14 sm:py-16 lg:py-20">
        <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-[22px] font-semibold tracking-tight text-ink sm:text-3xl lg:text-4xl">
              {t("partners.benefitsTitle", "Pourquoi rejoindre Tafwija ?")}
            </h2>
            <p className="mt-2 text-sm text-ink-muted sm:text-base">
              {t(
                "partners.benefitsSubtitle",
                "Un partenariat pensé pour développer votre activité en toute simplicité."
              )}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {BENEFITS.map((benefit) => (
              <BenefitCard key={benefit.titleKey} benefit={benefit} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* How it works + contact — two-up on desktop */}
      <section className="relative pb-16 sm:pb-20">
        <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr] lg:gap-8">
            {/* Steps */}
            <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card sm:p-8">
              <h3 className="font-display text-lg font-semibold text-ink sm:text-xl">
                {t("partners.howTitle", "Comment ça marche")}
              </h3>
              <ol className="mt-6 space-y-5">
                {STEPS.map((step, i) => (
                  <li key={step.key} className="flex items-start gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-500 text-sm font-bold text-white">
                      {i + 1}
                    </span>
                    <p className="pt-1 text-sm font-medium leading-relaxed text-ink">
                      {t(step.key, step.def)}
                    </p>
                  </li>
                ))}
              </ol>
            </div>

            {/* Contact card */}
            <div className="relative overflow-hidden rounded-2xl border border-brand-100 bg-brand-500 p-6 shadow-card sm:p-8">
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-16 -end-16 h-48 w-48 rounded-full bg-accent-500/25 blur-2xl"
              />
              <div className="relative">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-white">
                  <ChatBubbleOvalLeftEllipsisIcon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-white sm:text-xl">
                  {t("partners.contactTitle", "Une question avant de commencer ?")}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-100">
                  {t(
                    "partners.contactSubtitle",
                    "Notre équipe partenaires est à votre écoute pour vous accompagner à chaque étape."
                  )}
                </p>

                <div className="mt-6 space-y-3">
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 transition-colors hover:bg-white/15"
                  >
                    <EnvelopeIcon className="h-5 w-5 shrink-0 text-accent-300" />
                    <span className="min-w-0 truncate text-sm font-medium text-white">
                      {CONTACT_EMAIL}
                    </span>
                  </a>
                  <a
                    href={`tel:${WHATSAPP_PHONE}`}
                    className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 transition-colors hover:bg-white/15"
                  >
                    <ChatBubbleOvalLeftEllipsisIcon className="h-5 w-5 shrink-0 text-accent-300" />
                    <span className="min-w-0 truncate text-sm font-medium text-white" dir="ltr">
                      WhatsApp · {WHATSAPP_PHONE}
                    </span>
                  </a>
                </div>

                <a
                  href={REGISTER_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-accent-500 px-6 font-semibold text-brand-900 shadow-card transition-colors hover:bg-accent-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <CheckIcon className="h-4 w-4" strokeWidth={2.5} />
                  <span>{t("partners.contactCta", "Commencer l'inscription")}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
